import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import L from "leaflet";
import { MapContainer, Marker, Popup, TileLayer, useMap } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import {
  Award,
  Building2,
  ChevronRight,
  GraduationCap,
  Languages,
  List,
  LocateFixed,
  Map,
  MapPin,
  Search,
  ShieldCheck,
  SlidersHorizontal,
  Sparkles,
  Stethoscope,
} from "lucide-react";
import api from "../lib/api";
import {
  PublicSiteFooter,
  PublicSiteHeader,
} from "../components/PublicSiteChrome";

const point = (c) =>
  c?.location?.coordinates?.length === 2
    ? [c.location.coordinates[1], c.location.coordinates[0]]
    : null;
const media = (path) =>
  path?.startsWith("/")
    ? `${(process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "")}${path}`
    : path;
const initials = (name) =>
  (name || "Clinic")
    .split(/\s+/)
    .slice(0, 2)
    .map((x) => x[0])
    .join("")
    .toUpperCase();
const markerIcon = (selected) =>
  L.divIcon({
    className: "",
    html: `<div style="width:${selected ? 46 : 40}px;height:${selected ? 46 : 40}px;border-radius:50% 50% 50% 0;transform:rotate(-45deg);background:${selected ? "#0f4c81" : "#2563eb"};border:4px solid white;box-shadow:0 8px 22px rgba(15,23,42,.3);display:flex;align-items:center;justify-content:center"><span style="transform:rotate(45deg);color:white;font-size:18px">●</span></div>`,
    iconSize: [46, 46],
    iconAnchor: [23, 44],
    popupAnchor: [0, -42],
  });

function Recenter({ center, zoom }) {
  const map = useMap();
  useEffect(() => {
    map.setView(center, zoom);
  }, [map, center, zoom]);
  return null;
}
function Shell({ children, embedded=false }) {
  if(embedded)return <div>{children}</div>;
  return (
    <div className="min-h-screen bg-slate-50 text-slate-800">
      <PublicSiteHeader />
      {children}
      <PublicSiteFooter />
    </div>
  );
}
function Hero({ doctor = false }) {
  return (
    <section className="relative overflow-hidden bg-gradient-to-br from-slate-950 via-mg-navy to-blue-900 text-white pt-32 pb-20 px-5">
      <div className="absolute -top-28 -right-24 w-96 h-96 rounded-full bg-blue-400/20 blur-3xl" />
      <div className="absolute -bottom-40 -left-20 w-96 h-96 rounded-full bg-emerald-400/15 blur-3xl" />
      <motion.div
        initial={{ opacity: 0, y: 18 }}
        animate={{ opacity: 1, y: 0 }}
        className="relative max-w-7xl mx-auto"
      >
        <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-white/15 bg-white/10 text-xs font-black uppercase tracking-wider">
          <Sparkles className="w-4 h-4 text-mg-gold" />
          {doctor ? "Doctor directory" : "Verified clinic directory"}
        </span>
        <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight max-w-3xl mt-6">
          {doctor ? "Meet the right doctor" : "Find trusted care"}{" "}
          <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-300 to-emerald-300">
            {doctor ? "for your care" : "close to home"}
          </span>
        </h1>
        <p className="mt-5 max-w-2xl text-base sm:text-lg text-slate-300 font-medium leading-relaxed">
          {doctor
            ? "Review professional profiles and choose the most convenient clinic for your appointment."
            : "Explore verified clinic locations and continue directly to simple online appointment booking."}
        </p>
      </motion.div>
    </section>
  );
}
function Empty({ type }) {
  const Icon = type === "clinic" ? Building2 : Stethoscope;
  return (
    <div className="rounded-3xl border border-dashed border-slate-300 bg-white p-12 text-center">
      <Icon className="w-8 h-8 text-mg-medical mx-auto" />
      <h3 className="font-extrabold text-mg-navy mt-4">
        No {type}s match your search
      </h3>
      <p className="text-sm text-slate-500 mt-2">
        Try a broader location or remove one of the filters.
      </p>
    </div>
  );
}

export function PublicClinicsDirectory({embedded=false}) {
  const [filters, setFilters] = useState({
    q: "",
    department: "",
    state: "",
    district: "",
    radius_km: 25,
  });
  const [query, setQuery] = useState(filters);
  const [geo, setGeo] = useState(null);
  const [locations, setLocations] = useState({ states: [], districts: [] });
  const [departments, setDepartments] = useState([]);
  const [data, setData] = useState({ items: [], total: 0 });
  const [selected, setSelected] = useState(null);
  const [view, setView] = useState("list");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  useEffect(() => {
    Promise.all([
      api.get("/public/directory/locations"),
      api.get("/public/directory/departments"),
    ])
      .then(([l, d]) => {
        setLocations(l.data);
        setDepartments(d.data.items || []);
      })
      .catch(() => {});
  }, []);
  useEffect(() => {
    setLoading(true);
    setError("");
    api
      .get("/public/directory/clinics", {
        params: {
          ...query,
          ...(geo ? { latitude: geo.latitude, longitude: geo.longitude } : {}),
        },
      })
      .then((r) => {
        setData(r.data);
        setSelected(
          (current) =>
            r.data.items.find((x) => x.clinic_id === current?.clinic_id) ||
            r.data.items.find(point) ||
            null,
        );
      })
      .catch((e) =>
        setError(e?.response?.data?.detail || "Clinics could not be loaded"),
      )
      .finally(() => setLoading(false));
  }, [query, geo]);
  const districts = useMemo(
    () =>
      locations.districts.filter(
        (x) => !filters.state || x.state === filters.state,
      ),
    [locations, filters.state],
  );
  const center =
    point(selected) ||
    (geo ? [geo.latitude, geo.longitude] : [20.5937, 78.9629]);
  const zoom = geo || point(selected) ? 12 : 5;
  const locate = () =>
    navigator.geolocation?.getCurrentPosition(
      ({ coords }) => {
        setGeo({ latitude: coords.latitude, longitude: coords.longitude });
        setView("map");
      },
      () =>
        setError(
          "Location access was not available. Allow location permission in your browser and try again.",
        ),
      { enableHighAccuracy: true, timeout: 12000 },
    );
  return (
    <Shell embedded={embedded}>
      <Hero />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-10 sm:py-14">
        <form
          onSubmit={(e) => {
            e.preventDefault();
            setQuery(filters);
          }}
          className="relative -mt-20 z-20 bg-white rounded-3xl shadow-2xl shadow-slate-900/10 border border-slate-100 p-4 sm:p-6"
        >
          <div className="flex items-center gap-2 mb-4 text-sm font-extrabold text-mg-navy">
            <SlidersHorizontal className="w-4 h-4 text-mg-medical" />
            Search clinics
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-[1.5fr_1fr_1fr_1fr_auto] gap-3">
            <label className="text-xs font-bold text-slate-600">
              Clinic name or location
              <div className="relative mt-1">
                <Search className="absolute left-3 top-3 w-4 h-4 text-slate-400 pointer-events-none" />
                <input
                  style={{ paddingLeft: "2.5rem" }}
                  className="mg-input w-full"
                  placeholder="Enter clinic, city, locality or postal code"
                  value={filters.q}
                  onChange={(e) =>
                    setFilters({ ...filters, q: e.target.value })
                  }
                />
              </div>
            </label>
            <label className="text-xs font-bold text-slate-600">
              Department
              <select
                className="mg-input w-full mt-1"
                value={filters.department}
                onChange={(e) =>
                  setFilters({ ...filters, department: e.target.value })
                }
              >
                <option value="">All departments</option>
                {departments.map((x) => (
                  <option key={x}>{x}</option>
                ))}
              </select>
            </label>
            <label className="text-xs font-bold text-slate-600">
              State
              <select
                className="mg-input w-full mt-1"
                value={filters.state}
                onChange={(e) =>
                  setFilters({
                    ...filters,
                    state: e.target.value,
                    district: "",
                  })
                }
              >
                <option value="">All states</option>
                {locations.states.map((x) => (
                  <option key={x}>{x}</option>
                ))}
              </select>
            </label>
            <label className="text-xs font-bold text-slate-600">
              District
              <select
                className="mg-input w-full mt-1"
                value={filters.district}
                onChange={(e) =>
                  setFilters({ ...filters, district: e.target.value })
                }
              >
                <option value="">All districts</option>
                {districts.map((x) => (
                  <option key={`${x.state}-${x.district}`} value={x.district}>
                    {x.district}
                  </option>
                ))}
              </select>
            </label>
            <div className="flex flex-col sm:flex-row lg:items-end gap-2">
              <button className="mg-btn-primary h-[42px] justify-center">
                <Search className="w-4 h-4" />
                Search clinics
              </button>
              <button
                type="button"
                onClick={locate}
                className="h-[42px] px-4 rounded-xl border border-blue-200 bg-blue-50 text-mg-medical flex items-center justify-center gap-2 text-sm font-extrabold hover:bg-blue-100"
              >
                <LocateFixed className="w-4 h-4" />
                Use my location
              </button>
            </div>
          </div>
          {geo && (
            <div className="mt-5 pt-4 border-t flex flex-col sm:flex-row gap-4 sm:items-center">
              <strong className="text-xs text-mg-navy whitespace-nowrap">
                Show clinics within {filters.radius_km} km
              </strong>
              <input
                aria-label="Search radius"
                type="range"
                min="2"
                max="200"
                value={filters.radius_km}
                onChange={(e) =>
                  setFilters({ ...filters, radius_km: Number(e.target.value) })
                }
                onMouseUp={() => setQuery(filters)}
                className="w-full accent-blue-600"
              />
              <button
                type="button"
                className="text-xs font-bold text-slate-500"
                onClick={() => setGeo(null)}
              >
                Clear my location
              </button>
            </div>
          )}
        </form>
        <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mt-9 mb-6">
          <div>
            <p className="text-xs uppercase tracking-wider font-black text-mg-medical">
              Clinic directory
            </p>
            <h2 className="text-2xl font-black text-mg-navy">
              {loading
                ? "Finding clinics…"
                : `${data.total} clinic${data.total === 1 ? "" : "s"} found`}
            </h2>
          </div>
          <div
            className="bg-white border border-slate-200 rounded-2xl p-1.5 flex shadow-sm"
            aria-label="Choose directory view"
          >
            <button
              onClick={() => setView("list")}
              className={`flex-1 sm:flex-none px-5 py-2.5 rounded-xl text-sm font-extrabold flex items-center justify-center gap-2 ${view === "list" ? "bg-mg-navy text-white shadow" : "text-slate-600"}`}
            >
              <List className="w-4 h-4" />
              List view
            </button>
            <button
              onClick={() => setView("map")}
              className={`flex-1 sm:flex-none px-5 py-2.5 rounded-xl text-sm font-extrabold flex items-center justify-center gap-2 ${view === "map" ? "bg-mg-navy text-white shadow" : "text-slate-600"}`}
            >
              <Map className="w-4 h-4" />
              Map view
            </button>
          </div>
        </div>
        {error && (
          <div className="mb-5 rounded-2xl bg-rose-50 border border-rose-100 text-rose-700 p-4 text-sm font-semibold">
            {error}
          </div>
        )}
        {view === "list" ? (
          <section className="grid md:grid-cols-2 xl:grid-cols-3 gap-5">
            {!loading && !data.items.length && (
              <div className="md:col-span-2 xl:col-span-3">
                <Empty type="clinic" />
              </div>
            )}
            {data.items.map((clinic) => (
              <article
                key={clinic.clinic_id}
                className="group bg-white rounded-3xl border border-slate-100 p-6 shadow-sm hover:shadow-xl hover:-translate-y-1 transition-all flex flex-col"
              >
                <div className="flex gap-4">
                  <div className="w-20 h-20 shrink-0 rounded-2xl bg-white border border-slate-200 shadow-sm overflow-hidden flex items-center justify-center">
                    {clinic.logo_url ? (
                      <img
                        src={media(clinic.logo_url)}
                        alt={`${clinic.name} logo`}
                        className="w-full h-full object-contain p-2"
                      />
                    ) : (
                      <span className="text-xl font-black text-mg-medical">
                        {initials(clinic.name)}
                      </span>
                    )}
                  </div>
                  <div className="min-w-0">
                    <h3 className="text-lg font-extrabold text-mg-navy">
                      {clinic.name}
                    </h3>
                    <p className="text-sm text-slate-500 mt-1 flex gap-1">
                      <MapPin className="w-4 h-4 shrink-0 text-emerald-500" />
                      {[clinic.locality, clinic.district, clinic.state]
                        .filter(Boolean)
                        .join(", ") || clinic.address}
                    </p>
                    {clinic.distance_km !== undefined && (
                      <span className="inline-block mt-2 rounded-full bg-emerald-50 text-emerald-700 px-3 py-1 text-xs font-extrabold">
                        {clinic.distance_km} km away
                      </span>
                    )}
                  </div>
                </div>
                {clinic.departments?.length > 0 && (
                  <div className="flex flex-wrap gap-1.5 mt-4">
                    {clinic.departments.slice(0, 4).map((x) => (
                      <span
                        key={x}
                        className="rounded-full bg-blue-50 text-blue-700 px-2.5 py-1 text-[11px] font-bold"
                      >
                        {x}
                      </span>
                    ))}
                  </div>
                )}
                {clinic.description && (
                  <p className="text-sm text-slate-600 leading-relaxed mt-4 line-clamp-3">
                    {clinic.description}
                  </p>
                )}
                {clinic.doctors?.length > 0 && (
                  <div className="mt-4 pt-4 border-t">
                    <p className="text-[11px] uppercase tracking-wider font-black text-slate-400">
                      Doctors practising here
                    </p>
                    <p className="text-sm text-mg-navy font-semibold mt-2">
                      {clinic.doctors
                        .map(
                          (d) =>
                            `${d.name}${d.specialization ? ` · ${d.specialization}` : ""}`,
                        )
                        .join("  •  ")}
                    </p>
                  </div>
                )}
                {clinic.services?.length > 0 && (
                  <p className="text-xs text-slate-500 mt-3">
                    <strong>Services:</strong>{" "}
                    {clinic.services.map((s) => s.name).join(", ")}
                  </p>
                )}
                {!clinic.online_booking_available && (
                  <p className="mt-3 rounded-xl bg-amber-50 border border-amber-100 px-3 py-2 text-xs font-bold text-amber-800">
                    Online booking unavailable—contact clinic.
                  </p>
                )}
                <Link
                  to={`/clinic/${clinic.slug}`}
                  className="mg-btn-primary w-full justify-center mt-auto pt-3"
                >
                  {clinic.online_booking_available ? "View clinic & book" : "View clinic"}
                  <ChevronRight className="w-4 h-4" />
                </Link>
              </article>
            ))}
          </section>
        ) : (
          <section className="h-[580px] rounded-3xl overflow-hidden border-4 border-white shadow-xl bg-slate-100">
            <MapContainer center={center} zoom={zoom} className="h-full w-full">
              <TileLayer
                attribution="&copy; OpenStreetMap contributors"
                url="https://tile.openstreetmap.org/{z}/{x}/{y}.png"
              />
              <Recenter center={center} zoom={zoom} />
              {data.items.filter(point).map((clinic) => (
                <Marker
                  key={clinic.clinic_id}
                  position={point(clinic)}
                  icon={markerIcon(selected?.clinic_id === clinic.clinic_id)}
                  eventHandlers={{ click: () => setSelected(clinic) }}
                >
                  <Popup minWidth={270}>
                    <div className="p-1">
                      <div className="flex gap-3 items-center">
                        <div className="w-12 h-12 border rounded-xl flex items-center justify-center overflow-hidden">
                          {clinic.logo_url ? (
                            <img
                              src={media(clinic.logo_url)}
                              alt=""
                              className="w-full h-full object-contain p-1"
                            />
                          ) : (
                            <strong>{initials(clinic.name)}</strong>
                          )}
                        </div>
                        <div>
                          <strong className="text-sm text-slate-900">
                            {clinic.name}
                          </strong>
                          <p className="text-xs text-slate-500 m-0">
                            {[clinic.locality, clinic.district]
                              .filter(Boolean)
                              .join(", ")}
                          </p>
                        </div>
                      </div>
                      {clinic.departments?.length > 0 && (
                        <div className="flex flex-wrap gap-1 mt-3">
                          {clinic.departments.slice(0, 4).map((department) => (
                            <span key={department} className="rounded-full bg-blue-50 px-2 py-1 text-[10px] font-bold text-blue-700">
                              {department}
                            </span>
                          ))}
                        </div>
                      )}
                      {clinic.description && (
                        <p className="mt-3 mb-0 text-xs leading-relaxed text-slate-600 line-clamp-3">
                          {clinic.description}
                        </p>
                      )}
                      {clinic.services?.length > 0 && (
                        <p className="mt-2 mb-0 text-[11px] text-slate-500">
                          <strong>Services:</strong> {clinic.services.slice(0, 4).map((service) => service.name).join(", ")}
                        </p>
                      )}
                      {!clinic.online_booking_available && (
                        <p className="mt-2 mb-0 rounded-lg bg-amber-50 px-2 py-2 text-[11px] font-bold text-amber-800">
                          Online booking unavailable—contact clinic.
                        </p>
                      )}
                      <a
                        href={`/clinic/${clinic.slug}`}
                        className="block mt-3 rounded-lg bg-blue-600 text-white text-center font-bold px-3 py-2"
                      >
                        {clinic.online_booking_available ? "View clinic & book" : "View clinic"}
                      </a>
                    </div>
                  </Popup>
                </Marker>
              ))}
            </MapContainer>
          </section>
        )}
      </main>
    </Shell>
  );
}

export function PublicDoctorsDirectory({embedded=false}) {
  const [filters, setFilters] = useState({
    q: "",
    specialization: "",
    state: "",
    district: "",
  });
  const [query, setQuery] = useState(filters);
  const [data, setData] = useState({ items: [], total: 0 });
  const [locations, setLocations] = useState({ states: [], districts: [] });
  const [specialties, setSpecialties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  useEffect(() => {
    Promise.all([
      api.get("/public/directory/locations"),
      api.get("/public/directory/specialties"),
    ])
      .then(([l, s]) => {
        setLocations(l.data);
        setSpecialties(s.data.items || []);
      })
      .catch(() => {});
  }, []);
  useEffect(() => {
    setLoading(true);
    setError("");
    api
      .get("/public/directory/doctors", { params: query })
      .then((r) => setData(r.data))
      .catch((e) =>
        setError(e?.response?.data?.detail || "Doctors could not be loaded"),
      )
      .finally(() => setLoading(false));
  }, [query]);
  const districts = useMemo(
    () =>
      locations.districts.filter(
        (x) => !filters.state || x.state === filters.state,
      ),
    [locations, filters.state],
  );
  return (
    <Shell embedded={embedded}>
      <Hero doctor />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-10 sm:py-14">
        <form
          onSubmit={(e) => {
            e.preventDefault();
            setQuery(filters);
          }}
          className="relative -mt-20 z-20 bg-white rounded-3xl shadow-2xl shadow-slate-900/10 border border-slate-100 p-4 sm:p-6"
        >
          <div className="flex items-center gap-2 mb-4 text-sm font-extrabold text-mg-navy">
            <SlidersHorizontal className="w-4 h-4 text-mg-medical" />
            Search doctors
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-[1.4fr_1fr_1fr_1fr_auto] gap-3">
            <label className="text-xs font-bold text-slate-600">
              Doctor name or qualification
              <input
                className="mg-input w-full mt-1"
                placeholder="Enter doctor name or qualification"
                value={filters.q}
                onChange={(e) => setFilters({ ...filters, q: e.target.value })}
              />
            </label>
            <label className="text-xs font-bold text-slate-600">
              Medical speciality
              <select
                className="mg-input w-full mt-1"
                value={filters.specialization}
                onChange={(e) =>
                  setFilters({ ...filters, specialization: e.target.value })
                }
              >
                <option value="">All specialities</option>
                {specialties.map((x) => (
                  <option key={x} value={x}>
                    {x}
                  </option>
                ))}
              </select>
            </label>
            <label className="text-xs font-bold text-slate-600">
              State
              <select
                className="mg-input w-full mt-1"
                value={filters.state}
                onChange={(e) =>
                  setFilters({
                    ...filters,
                    state: e.target.value,
                    district: "",
                  })
                }
              >
                <option value="">All states</option>
                {locations.states.map((x) => (
                  <option key={x}>{x}</option>
                ))}
              </select>
            </label>
            <label className="text-xs font-bold text-slate-600">
              District
              <select
                className="mg-input w-full mt-1"
                value={filters.district}
                onChange={(e) =>
                  setFilters({ ...filters, district: e.target.value })
                }
              >
                <option value="">All districts</option>
                {districts.map((x) => (
                  <option key={`${x.state}-${x.district}`} value={x.district}>
                    {x.district}
                  </option>
                ))}
              </select>
            </label>
            <button className="mg-btn-primary self-end h-[42px] justify-center">
              <Search className="w-4 h-4" />
              Search
            </button>
          </div>
        </form>
        <div className="mt-10 mb-6">
          <p className="text-xs uppercase tracking-wider font-black text-mg-medical">
            Doctor directory
          </p>
          <h2 className="text-2xl sm:text-3xl font-black text-mg-navy">
            {loading
              ? "Finding doctors…"
              : `${data.total} doctor${data.total === 1 ? "" : "s"} found`}
          </h2>
        </div>
        {error && (
          <div className="mb-5 rounded-2xl bg-rose-50 border border-rose-100 text-rose-700 p-4">
            {error}
          </div>
        )}
        {!loading && !data.items.length ? (
          <Empty type="doctor" />
        ) : (
          <section className="grid md:grid-cols-2 xl:grid-cols-3 gap-5">
            {data.items.map((doctor) => (
              <article
                key={doctor.public_profile_id}
                className="group bg-white rounded-3xl border border-slate-100 shadow-sm hover:shadow-xl hover:-translate-y-1 transition-all overflow-hidden"
              >
                <div className="h-24 bg-gradient-to-r from-blue-50 via-cyan-50 to-emerald-50" />
                <div className="px-6 pb-6 -mt-12">
                  <div className="w-24 h-24 rounded-3xl bg-white border-4 border-white shadow-lg overflow-hidden flex items-center justify-center">
                    {doctor.profile_image_path ? (
                      <img
                        src={media(doctor.profile_image_path)}
                        alt={doctor.name}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <Stethoscope className="w-9 h-9 text-mg-medical" />
                    )}
                  </div>
                  <h3 className="text-xl font-black text-mg-navy mt-4">
                    {doctor.name}
                  </h3>
                  <p className="text-sm font-extrabold text-mg-medical mt-1">
                    {doctor.specialization || "Medical practitioner"}
                  </p>
                  <p className="flex items-start gap-2 mt-3 text-sm text-slate-500">
                    <GraduationCap className="w-4 h-4 mt-0.5 shrink-0" />
                    {doctor.qualification ||
                      "Qualification available on profile"}
                  </p>
                  <div className="mt-5 pt-4 border-t">
                    <p className="text-xs uppercase tracking-wider font-black text-slate-400 mb-3">
                      Practising at
                    </p>
                    <div className="space-y-2">
                      {doctor.practices.slice(0, 2).map((p) => (
                        <div
                          key={p.doctor_id}
                          className="rounded-2xl bg-slate-50 p-3"
                        >
                          <strong className="text-sm text-mg-navy">
                            {p.clinic.name}
                          </strong>
                          <p className="text-xs text-slate-500 mt-1 flex gap-1">
                            <MapPin className="w-3 h-3" />
                            {[p.clinic.locality, p.clinic.district]
                              .filter(Boolean)
                              .join(", ")}
                          </p>
                          {!p.clinic.online_booking_available && (
                            <p className="text-[11px] font-bold text-amber-700 mt-2">Online booking unavailable—contact clinic.</p>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                  <div className="flex gap-2 mt-5">
                    <Link
                      to={`/doctors/${doctor.public_profile_id}`}
                      className="mg-btn-secondary text-xs flex-1 justify-center"
                    >
                      Full profile
                    </Link>
                    <Link
                      to={`/clinic/${doctor.practices[0]?.clinic.slug}`}
                      className="mg-btn-primary text-xs flex-1 justify-center"
                    >
                      {doctor.practices[0]?.clinic.online_booking_available ? "Book now" : "View clinic"}
                    </Link>
                  </div>
                </div>
              </article>
            ))}
          </section>
        )}
      </main>
    </Shell>
  );
}

export function PublicDoctorDetail() {
  const { id } = useParams();
  const [doctor, setDoctor] = useState(null);
  const [error, setError] = useState("");
  useEffect(() => {
    setError("");
    api
      .get(`/public/directory/doctors/${id}`)
      .then((r) => setDoctor(r.data))
      .catch((e) =>
        setError(
          e?.response?.data?.detail || "Doctor profile could not be loaded",
        ),
      );
  }, [id]);
  return (
    <Shell>
      <main className="max-w-5xl mx-auto px-4 sm:px-6 pt-32 pb-16">
        {error ? (
          <div className="mg-card text-rose-700">
            <h1 className="font-black text-xl">Profile unavailable</h1>
            <p className="mt-2">{error}</p>
            <Link to="/doctors" className="mg-btn-secondary mt-4">
              Back to doctors
            </Link>
          </div>
        ) : !doctor ? (
          <div className="mg-card">Loading doctor profile…</div>
        ) : (
          <>
            <Link to="/doctors" className="text-sm font-bold text-mg-medical">
              ← Back to doctors
            </Link>
            <section className="mt-5 bg-white rounded-3xl border shadow-xl overflow-hidden">
              <div className="h-36 bg-gradient-to-r from-slate-950 via-blue-900 to-emerald-800" />
              <div className="px-6 sm:px-10 pb-10 -mt-16">
                <div className="w-32 h-32 rounded-3xl bg-white border-4 border-white shadow-xl overflow-hidden flex items-center justify-center">
                  {doctor.profile_image_path ? (
                    <img
                      src={media(doctor.profile_image_path)}
                      alt={doctor.name}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <Stethoscope className="w-12 h-12 text-mg-medical" />
                  )}
                </div>
                <p className="text-xs uppercase tracking-wider font-black text-mg-medical mt-5 flex gap-2">
                  <ShieldCheck className="w-4 h-4" />
                  Verified professional profile
                </p>
                <h1 className="text-3xl sm:text-4xl font-black text-mg-navy mt-1">
                  {doctor.name}
                </h1>
                <p className="text-lg font-bold text-mg-medical mt-2">
                  {doctor.specialization}
                  {doctor.sub_specialization
                    ? ` · ${doctor.sub_specialization}`
                    : ""}
                </p>
                <div className="flex flex-wrap gap-3 mt-5 text-sm text-slate-600">
                  <span className="inline-flex gap-2 items-center rounded-full bg-slate-100 px-4 py-2">
                    <Award className="w-4 h-4" />
                    {doctor.qualification || "Qualification on file"}
                  </span>
                  {doctor.years_of_experience != null && (
                    <span className="rounded-full bg-slate-100 px-4 py-2 font-semibold">
                      {doctor.years_of_experience} years experience
                    </span>
                  )}
                  {doctor.designation && (
                    <span className="rounded-full bg-slate-100 px-4 py-2 font-semibold">
                      {doctor.designation}
                    </span>
                  )}
                </div>
                {doctor.bio && (
                  <div className="mt-7">
                    <h2 className="font-black text-mg-navy">
                      About the doctor
                    </h2>
                    <p className="mt-2 text-slate-600 leading-relaxed max-w-3xl">
                      {doctor.bio}
                    </p>
                  </div>
                )}
                <div className="grid sm:grid-cols-2 gap-4 mt-7">
                  {doctor.registration_number && (
                    <div className="rounded-2xl bg-blue-50 p-4">
                      <p className="text-xs font-black uppercase text-blue-600">
                        Medical registration
                      </p>
                      <p className="font-bold mt-1">
                        {doctor.registration_number}
                        {doctor.registration_council
                          ? ` · ${doctor.registration_council}`
                          : ""}
                      </p>
                    </div>
                  )}
                  {doctor.languages?.length > 0 && (
                    <div className="rounded-2xl bg-emerald-50 p-4">
                      <p className="text-xs font-black uppercase text-emerald-700 flex gap-2">
                        <Languages className="w-4 h-4" />
                        Languages
                      </p>
                      <p className="font-bold mt-1">
                        {doctor.languages.join(", ")}
                      </p>
                    </div>
                  )}
                </div>
                <div className="mt-9 border-t pt-7">
                  <h2 className="text-xl font-black text-mg-navy">
                    Clinics and appointment locations
                  </h2>
                  <p className="text-sm text-slate-500 mt-1">
                    Choose where you would like to consult this doctor.
                  </p>
                  <div className="grid md:grid-cols-2 gap-4 mt-4">
                    {doctor.practices.map((p) => (
                      <article
                        key={p.doctor_id}
                        className="rounded-2xl border p-5"
                      >
                        <div className="flex gap-3">
                          <div className="w-11 h-11 rounded-xl bg-blue-50 text-mg-medical flex items-center justify-center">
                            <Building2 className="w-5 h-5" />
                          </div>
                          <div>
                            <h3 className="font-extrabold text-mg-navy">
                              {p.clinic.name}
                            </h3>
                            <p className="text-sm text-slate-500 mt-1">
                              {p.clinic.address ||
                                [
                                  p.clinic.locality,
                                  p.clinic.district,
                                  p.clinic.state,
                                ]
                                  .filter(Boolean)
                                  .join(", ")}
                            </p>
                            {p.consultation_fee > 0 && (
                              <p className="text-sm font-bold text-emerald-700 mt-2">
                                Consultation fee ₹{p.consultation_fee}
                              </p>
                            )}
                            {!p.clinic.online_booking_available && (
                              <p className="text-xs font-bold text-amber-700 mt-2">Online booking unavailable—contact clinic.</p>
                            )}
                          </div>
                        </div>
                        <Link
                          className="mg-btn-primary text-sm w-full justify-center mt-5"
                          to={`/clinic/${p.clinic.slug}`}
                        >
                          {p.clinic.online_booking_available ? "Book at this clinic" : "View clinic contact details"}
                          <ChevronRight className="w-4 h-4" />
                        </Link>
                      </article>
                    ))}
                  </div>
                </div>
              </div>
            </section>
          </>
        )}
      </main>
    </Shell>
  );
}
